<script setup>
// Lanzador de Salesbots vía "/" en el editor · rediseño P2 (Victor 2026-05-24):
// dropdown AGRUPADO POR CATEGORÍA, sin el ruido de "Manual · X% conv · N runs".
// Cada fila = monograma + nombre + descripción (qué hace el bot). Navegación
// ↑↓ y Enter para enviar vía useKeyboardNavigableList (igual que MentionBox).
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import BotsAPI from 'dashboard/api/bots';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';

const props = defineProps({
  searchKey: { type: String, default: '' },
});
const emit = defineEmits(['launch']);

const bots = ref([]);
const listRef = ref(null);
const selectedIndex = ref(0);

// Vista agrupada: { groups: [{ category, rows:[{id,name,description,flatIndex}] }],
// flat: [...] }. `flat` mantiene el orden visual para que ↑↓ recorra todo
// (las cabeceras de grupo NO son navegables).
const view = computed(() => {
  const q = (props.searchKey || '').trim().toLowerCase();
  const filtered = bots.value.filter(
    b => b.active && (!q || b.name.toLowerCase().includes(q))
  );
  const map = new Map();
  filtered.forEach(b => {
    const cat = (b.category && String(b.category).trim()) || 'Otros';
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat).push(b);
  });
  const groups = [];
  const flat = [];
  map.forEach((arr, category) => {
    const rows = arr.map(b => {
      const item = {
        id: b.id,
        name: b.name,
        description: (b.description || '').trim(),
        flatIndex: flat.length,
      };
      flat.push(item);
      return item;
    });
    groups.push({ category, rows });
  });
  return { groups, flat };
});

const flatItems = computed(() => view.value.flat);

const monogram = name => (name || '?').trim().charAt(0).toUpperCase();

const kbdStyle =
  'font-size:10px;font-weight:700;color:#9a9db0;background:rgba(255,255,255,0.06);' +
  'border:1px solid rgba(255,255,255,0.07);border-radius:5px;padding:2px 6px;' +
  'min-width:18px;text-align:center';

const adjustScroll = () => {
  nextTick(() => {
    const el = listRef.value?.querySelector(`#bot-item-${selectedIndex.value}`);
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
  });
};

const onSelect = () => {
  const item = flatItems.value[selectedIndex.value];
  if (item) emit('launch', item.id);
};

useKeyboardNavigableList({
  items: flatItems,
  onSelect,
  adjustScroll,
  selectedIndex,
});

// Si la búsqueda recorta la lista, no dejar el índice fuera de rango.
watch(flatItems, list => {
  if (selectedIndex.value > list.length - 1) selectedIndex.value = 0;
});

const onHover = index => {
  selectedIndex.value = index;
};
const onClick = index => {
  selectedIndex.value = index;
  onSelect();
};

onMounted(async () => {
  try {
    const res = await BotsAPI.get();
    bots.value = res.data?.payload || res.data || [];
  } catch (e) {
    bots.value = [];
  }
});
</script>

<template>
  <div
    v-if="flatItems.length"
    ref="listRef"
    class="absolute bottom-full left-0 z-20 w-full mb-1 overflow-auto rounded-2xl"
    style="
      max-height: 16rem;
      background: #141416;
      border: 1px solid rgba(255, 255, 255, 0.07);
      box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
    "
  >
    <!-- Encabezado -->
    <div
      class="flex items-center gap-2 px-3.5 py-2.5"
      style="border-bottom: 1px solid rgba(255, 255, 255, 0.07)"
    >
      <span
        class="font-extrabold uppercase"
        style="font-size: 10.5px; letter-spacing: 0.13em; color: #c9a35a"
        >⚡ Salesbots</span
      >
      <span class="ml-auto" style="font-size: 11px; color: #6b6e7d"
        >{{ flatItems.length }} {{ flatItems.length === 1 ? 'bot' : 'bots' }}</span
      >
    </div>

    <!-- Grupos por categoría -->
    <template v-for="group in view.groups" :key="group.category">
      <div
        class="px-3.5 pt-2.5 pb-1.5 font-extrabold uppercase"
        style="
          font-size: 10.5px;
          letter-spacing: 0.1em;
          color: #6b6e7d;
          background: rgba(255, 255, 255, 0.015);
          border-bottom: 1px solid rgba(255, 255, 255, 0.04);
        "
      >
        {{ group.category }}
      </div>
      <button
        v-for="row in group.rows"
        :id="`bot-item-${row.flatIndex}`"
        :key="row.id"
        type="button"
        class="flex items-center w-full gap-3 px-3.5 py-2.5 text-left cursor-pointer"
        style="border-bottom: 1px solid rgba(255, 255, 255, 0.04)"
        :style="
          row.flatIndex === selectedIndex
            ? 'box-shadow: inset 3px 0 0 #c9a35a; background: linear-gradient(90deg, rgba(201,163,90,0.10), rgba(201,163,90,0.02));'
            : ''
        "
        @mouseover="onHover(row.flatIndex)"
        @click="onClick(row.flatIndex)"
      >
        <span
          class="flex items-center justify-center shrink-0 font-extrabold"
          style="
            width: 34px;
            height: 34px;
            border-radius: 10px;
            font-size: 14px;
            color: #e3c98a;
            background: rgba(201, 163, 90, 0.1);
            border: 1px solid rgba(201, 163, 90, 0.28);
          "
          >{{ monogram(row.name) }}</span
        >
        <span class="flex-1 min-w-0">
          <span
            class="block overflow-hidden font-bold text-ellipsis whitespace-nowrap"
            style="font-size: 14px; color: #ecedf2"
            >{{ row.name }}</span
          >
          <span
            v-if="row.description"
            class="block overflow-hidden text-ellipsis whitespace-nowrap"
            style="font-size: 12px; color: #9a9db0; margin-top: 2px"
            >{{ row.description }}</span
          >
        </span>
        <span
          v-if="row.flatIndex === selectedIndex"
          style="color: #c9a35a; font-size: 13px"
          >↵</span
        >
      </button>
    </template>

    <!-- Atajos de teclado -->
    <div
      class="flex items-center gap-4 px-3.5 py-2.5"
      style="
        border-top: 1px solid rgba(255, 255, 255, 0.07);
        background: #17171a;
        font-size: 11px;
        color: #6b6e7d;
      "
    >
      <span class="inline-flex items-center gap-1.5">
        <kbd :style="kbdStyle">↑</kbd><kbd :style="kbdStyle">↓</kbd> navegar
      </span>
      <span class="inline-flex items-center gap-1.5">
        <kbd :style="kbdStyle">↵</kbd> enviar
      </span>
      <span class="inline-flex items-center gap-1.5">
        <kbd :style="kbdStyle">esc</kbd> cerrar
      </span>
    </div>
  </div>
</template>
